/* KycSectionOwnershipComponents/ControllingPersonsSection.jsx */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Tooltip } from "react-tooltip";
import CountrySelect from "../../../../components/ui/CountrySelect";
import DatePickerField from "../../../../components/ui/DatePickerField";
import IndividualNamePreview from "../../../../components/clients/IndividualNamePreview";
import OwnershipPercentageField from './OwnershipPercentageField';
import { arrangementPercentageContext, controllingPersonPercentageContext, linkedEntityHoldsPercent } from '../../../../utils/editOwnershipPercentage';

import iconIndividual from "../../../../images/avatar.svg";
import iconEntity from "../../../../images/businessTrade.svg";
import iconPassport from "../../../../images/passport.svg";
import iconAddress from "../../../../images/earth-location.svg";
import iconDateOfBirth from "../../../../images/birthday.svg";
import iconTrust from "../../../../images/certificate.svg";
import iconSettlor from "../../../../images/settlor.svg";
import iconTrustee from "../../../../images/handshake.svg";
import iconBeneficiary from "../../../../images/beneficiary.svg";
import iconRightArrow from "../../../../images/right-arrow.svg";
import iconQualifiedUbo from "../../../../images/qualified-g.svg";
import { isQualifiedUboRow } from "../../../../utils/qualifiedUboMarkers";

/* -------------------- Helpers -------------------- */

function safeClone(obj) {
  try {
    if (typeof structuredClone === "function") return structuredClone(obj);
  } catch (_) {}
  return JSON.parse(JSON.stringify(obj ?? {}));
}

function emptyRow(keys) {
  const o = {};
  keys.forEach((k) => (o[k] = ""));
  return o;
}

function makeRowId(prefix = "row") {
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function ensureLegalArrangementsShape(ownership) {
  const o = ownership && typeof ownership === "object" ? ownership : {};
  const la =
    o.legalArrangements && typeof o.legalArrangements === "object"
      ? o.legalArrangements
      : {};
  const arrangements = Array.isArray(la.arrangements) ? la.arrangements : [];
  return { arrangements };
}

function normalizeArrangementType(v) {
  const s = String(v || "")
    .trim()
    .toLowerCase();
  if (s === "foundation") return "Foundation";
  return "Trust";
}

function baseRolesForType(arrType) {
  const t = normalizeArrangementType(arrType);
  if (t === "Foundation") return ["Founder", "Council member", "Beneficiary"];
  return ["Settlor", "Trustee", "Beneficiary"];
}

function makeDefaultPartiesForType(arrType, { autoCreated = false } = {}) {
  return baseRolesForType(arrType).map((role) => ({
    _rowId: makeRowId("laP"),
    role,
    name: "",
    trusteeKind: "",
    nationality: "",
    domicile: "",
    autoCreated,
  }));
}

function trusteeKindLabel(kind) {
  return normalizeTrusteeKind(kind) === "company"
    ? "Company"
    : "Individual";
}

function normalizeTrusteeKind(kind) {
  const s = String(kind || "").trim().toLowerCase();
  if (s === "company" || s === "entity" || s === "corporate") {
    return "company";
  }
  if (s === "individual" || s === "person" || s === "natural person") {
    return "individual";
  }
  return "";
}

function toTitleCase(value) {
  return String(value || "").replace(/(^|\s)\S/g, (letter) =>
    letter.toUpperCase()
  );
}

function toDobDate(value) {
  if (!value) return null;
  let date = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (typeof value?.seconds === "number") {
    date = new Date(value.seconds * 1000);
  } else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value);
  }
  return !date || Number.isNaN(date.getTime()) ? null : date;
}

function formatCompactDob(value) {
  const date = toDobDate(value);
  return date ? date.toLocaleDateString('en-GB', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  }).replace(/\//g, '.') : '';
}

function formatTinyDob(value) {
  const date = toDobDate(value);
  if (!date) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function QualifiedUboBadge({ className = "kycQualifiedUboAvatarBadge" } = {}) {
  return (
    <img
      src={iconQualifiedUbo}
      alt="Qualified UBO"
      title="Qualified UBO"
      className={className}
    />
  );
}

function sanitizePercent(v) {
  let s = String(v ?? "").replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  if (s === "") return "";
  const n = Number(s);
  if (!Number.isFinite(n)) return "";
  return String(Math.max(0, Math.min(100, n)));
}

function percentNumber(v) {
  const n = Number(String(v ?? "").replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function formatPercentValue(n) {
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 100) / 100);
}

function optionLabelById(options, id) {
  const s = String(id || "");
  if (s === "__other__") return "Other form of control";
  const found = (options || []).find((o) => String(o.v) === s);
  return found ? String(found.label || "") : "";
}

const _cpDisplayNames = new Intl.DisplayNames(["en"], { type: "region" });
function countryLabel(code) {
  if (!code) return "";
  try { return _cpDisplayNames.of(String(code).toUpperCase()) || code; } catch { return code; }
}

function cpRoleIcon(role) {
  const r = String(role || "").trim().toLowerCase();
  if (r === "trustee" || r === "council member") return iconTrustee;
  if (r === "beneficiary") return iconBeneficiary;
  return iconSettlor;
}

function makeManualArrangement(type, { entityLink } = {}) {
  const t = normalizeArrangementType(type);

  const linkedId =
    entityLink && entityLink.rowId ? String(entityLink.rowId) : "";
  const linkedNodeId =
    entityLink && entityLink.nodeId ? String(entityLink.nodeId) : "";
  const linkedName =
    entityLink && entityLink.name ? String(entityLink.name) : "";

  return {
    _rowId: makeRowId("la"),
    arrangementType: t,
    name: "",
    source: linkedId ? "entity_manual" : "manual",
    sourceRefRowId: linkedId,
    sourceRefNodeId: linkedNodeId,
    relatedShareholderName: linkedName,
    autoCreated: false,
    parties: makeDefaultPartiesForType(t, { autoCreated: false }),
    _autoName: "",
  };
}

function hasOptionalProtector(parties) {
  return (Array.isArray(parties) ? parties : []).some(
    (p) => String(p?.role || "") === "Protector"
  );
}

/* -------------------- component -------------------- */

export default function ControllingPersonsSection({
  readOnly = false,
  separateNames = false,
  ownership,
  onSetAt,
  ownershipSnapshot,
  onOwnershipPercentageChange,
  // Client e-form: dates of birth are taken by staff from the identity documents.
  hideDateOfBirth = false,

  showNaturalPersons = false,
  showLegalArrangements = false,

  allowAddButtons = false,

  // Select + optional Other + entities (ONLY entities will link arrangements)
  relatedEntityOptions = [{ v: "", label: "Select" }],

  entityName = "Account holder",
  accountHolderIsTF = false,
  accountHolderTFType = "Trust",
  accountHolderTrustResolutionKind = "",
  hideSectionHeadings = false,

  naturalPersonsWrapClassName = "",
  changedUboRowIds = [],

  clientMode = false,
  qualifiedNodeIds = new Set(),
}) {
  const uboKeys = useMemo(
    () => [
      "relatedShareholderRowId",
      "name",
      "nationality",
      "countryOfResidence",
      "basisOfControl",
      "effectiveOwnershipPercent",
    ],
    []
  );

  const BASIS_OPTIONS = useMemo(
    () => [
      { v: "", label: "Select" },
      { v: "Indirect ownership", label: "Indirect ownership" },
      { v: "Managing director", label: "Managing director" },
      { v: "Control via agreement", label: "Control via agreement" },
      { v: "Other", label: "Other" },
    ],
    []
  );

  const countrySelectLikeInputClass = "w-100 countryLikeInput";

  const ubos = Array.isArray(ownership?.ubos) ? ownership.ubos : [];
  const legalArr = ensureLegalArrangementsShape(ownership);
  const legalArrangements = Array.isArray(legalArr.arrangements)
    ? legalArr.arrangements
    : [];

  const effectiveShowNaturalPersons = !!showNaturalPersons || ubos.length > 0;
  const effectiveShowLegalArrangements =
    !!showLegalArrangements || legalArrangements.length > 0;

  // ✅ Single toolbar selection used by ALL buttons
  const [selectedRelated, setSelectedRelated] = useState("");
  const [trusteeKindModal, setTrusteeKindModal] = useState({
    open: false,
    arrIdx: -1,
    partyId: "",
    value: "",
  });
  const trusteeInputRefs = useRef({});
  const [openBasisRowId, setOpenBasisRowId] = useState(null);
  const [basisDropdownPos, setBasisDropdownPos] = useState({ top: 0, left: 0 });
  const basisBtnRefs = useRef({});
  const [relatedDropdownOpen, setRelatedDropdownOpen] = useState(false);
  const [relatedDropdownPos, setRelatedDropdownPos] = useState({ top: 0, left: 0 });
  const relatedBtnRef = useRef(null);
  const changedUboRowIdSet = new Set(
    (Array.isArray(changedUboRowIds) ? changedUboRowIds : []).map((id) =>
      String(id || "")
    )
  );
  const hasSelectedRelated = !!String(selectedRelated || "");
  const selectedRelatedOption = hasSelectedRelated
    ? (relatedEntityOptions || []).find(
        (o) => String(o.v || "") === String(selectedRelated)
      )
    : null;
  const selectedRelatedIsEntity =
    hasSelectedRelated &&
    selectedRelated !== "__other__" &&
    (relatedEntityOptions || []).some(
      (o) => String(o.v || "") === String(selectedRelated)
    );
  const selectableRelatedOptions = useMemo(
    () => (relatedEntityOptions || []).filter((o) => o.v !== "__other__"),
    [relatedEntityOptions]
  );
  const selectedRelatedLabel =
    selectedRelatedOption?.label || selectableRelatedOptions.find((o) => !o.v)?.label || "Select";

  function toggleRelatedDropdown() {
    if (readOnly) return;
    const rect = relatedBtnRef.current?.getBoundingClientRect?.();
    if (rect) {
      setRelatedDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setRelatedDropdownOpen((open) => !open);
  }

  useLayoutEffect(() => {
    if (!openBasisRowId && !relatedDropdownOpen) return undefined;

    const updateDropdownPositions = () => {
      if (openBasisRowId) {
        const rect =
          basisBtnRefs.current[openBasisRowId]?.getBoundingClientRect?.();
        if (!rect) {
          setOpenBasisRowId(null);
        } else {
          setBasisDropdownPos({ top: rect.bottom + 4, left: rect.left });
        }
      }

      if (relatedDropdownOpen) {
        const rect = relatedBtnRef.current?.getBoundingClientRect?.();
        if (!rect) {
          setRelatedDropdownOpen(false);
        } else {
          setRelatedDropdownPos({
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
      }
    };

    updateDropdownPositions();
    window.addEventListener("scroll", updateDropdownPositions, true);
    window.addEventListener("resize", updateDropdownPositions);
    return () => {
      window.removeEventListener("scroll", updateDropdownPositions, true);
      window.removeEventListener("resize", updateDropdownPositions);
    };
  }, [openBasisRowId, relatedDropdownOpen]);

  // Auto-select if only 1 entity exists in options; reset if current selection is stale
  useEffect(() => {
    const entityIds = (relatedEntityOptions || [])
      .map((o) => String(o.v || ""))
      .filter((v) => v && v !== "__other__");

    if (entityIds.length === 1) {
      const only = entityIds[0];
      setSelectedRelated((cur) => {
        const currentStillValid = cur && entityIds.includes(cur);
        return currentStillValid ? cur : only;
      });
    }
    // eslint-disable-next-line
  }, [(relatedEntityOptions || []).map((o) => `${o.v}:${o.label}`).join("|")]);

  function setUbos(next) {
    if (readOnly) return;
    onSetAt?.(["ownership", "ubos"], next || []);
  }

  function setLegalArrangements(nextArrangements) {
    if (readOnly) return;
    onSetAt?.(["ownership", "legalArrangements"], {
      arrangements: nextArrangements || [],
    });
  }

  function addNaturalPersonRow() {
    if (readOnly) return;
    const list = safeClone(ubos);
    const row = emptyRow(uboKeys);
    row._rowId = makeRowId("ubo");
    row.relatedShareholderRowId =
      selectedRelatedOption?.relatedShareholderRowId || selectedRelated || "";
    row.relatedShareholderNodeId =
      selectedRelatedOption?.relatedShareholderNodeId || "";
    // Under an operating company or active holding, a person is most often an indirect owner:
    // start there so the "% in the company" field shows at once (the basis can still be changed).
    if (!row.basisOfControl && linkedEntityHoldsPercent(row, ownership, ownershipSnapshot)) row.basisOfControl = "Indirect ownership";
    list.push(row);
    setUbos(list);
  }

  function updateUbo(idx, key, value) {
    if (readOnly) return;
    const list = safeClone(ubos);
    if (!list[idx]) list[idx] = {};

    if (key === "effectiveOwnershipPercent") {
      const sanitized = sanitizePercent(value);
      if (sanitized === "") {
        list[idx][key] = "";
        list[idx].ownershipPercent = "";
        setUbos(list);
        return;
      }

      const groupKey =
        String(list[idx]?.relatedShareholderRowId || "") ||
        String(list[idx]?.relatedShareholderNodeId || "") ||
        String(list[idx]?.relatedShareholderIdx || "");
      const otherTotal = list.reduce((sum, row, rowIdx) => {
        if (rowIdx === idx) return sum;
        const rowGroupKey =
          String(row?.relatedShareholderRowId || "") ||
          String(row?.relatedShareholderNodeId || "") ||
          String(row?.relatedShareholderIdx || "");
        if (rowGroupKey !== groupKey) return sum;
        return sum + percentNumber(row?.effectiveOwnershipPercent);
      }, 0);
      const maxForRow = Math.max(0, Math.round((100 - otherTotal) * 100) / 100);
      const nextValue = formatPercentValue(
        Math.min(percentNumber(sanitized), maxForRow)
      );
      list[idx][key] = nextValue;
      list[idx].ownershipPercent = nextValue;
      setUbos(list);
      return;
    }

    list[idx] = typeof key === "object" ? { ...list[idx], ...key } : { ...list[idx], [key]: value };
    setUbos(list);
  }

  function deleteUbo(rowId) {
    if (readOnly) return;
    const next = (ubos || []).filter(
      (r) => String(r?._rowId || "") !== String(rowId || "")
    );
    setUbos(next);
  }

  // ✅ Trust/Foundation: link ONLY if selectedRelated is an entity (not __other__)
  function addManualArrangement(type) {
    if (readOnly) return;

    const sel = String(selectedRelated || "");
    const isEntityId = !!sel && sel !== "__other__";
    if (!isEntityId) return;

    const opt = isEntityId
      ? (relatedEntityOptions || []).find((o) => String(o.v || "") === sel)
      : null;

    const entityLink =
      isEntityId && opt
        ? {
            rowId: opt.relatedShareholderRowId || sel,
            nodeId: opt.relatedShareholderNodeId || "",
            name: String(opt.label || ""),
          }
        : null;

    const la = ensureLegalArrangementsShape(ownership);
    const list = Array.isArray(la.arrangements) ? la.arrangements : [];
    const next = list.slice();
    next.push(makeManualArrangement(type, { entityLink }));
    setLegalArrangements(next);
  }

  // Atomic patch (still needed for edits + parties)
  function patchArrangement(arrIdx, patch) {
    if (readOnly) return;
    const la = ensureLegalArrangementsShape(ownership);
    const list = Array.isArray(la.arrangements) ? la.arrangements : [];
    if (!list[arrIdx]) return;

    const next = list.slice();
    let cur = { ...(next[arrIdx] || {}) };

    if (
      patch &&
      Object.prototype.hasOwnProperty.call(patch, "arrangementType")
    ) {
      const t = normalizeArrangementType(patch.arrangementType);
      cur.arrangementType = t;
      cur.parties = makeDefaultPartiesForType(t, {
        autoCreated: !!cur.autoCreated,
      });
    }

    Object.keys(patch || {}).forEach((k) => {
      if (k === "arrangementType") return;
      cur[k] = patch[k];
    });

    next[arrIdx] = cur;
    setLegalArrangements(next);
  }

  function updateArrangement(arrIdx, key, value) {
    patchArrangement(arrIdx, { [key]: value });
  }

  function openTrusteeKindModal(arrIdx, partyId, currentValue = "") {
    if (readOnly) return;
    const normalizedCurrentValue = normalizeTrusteeKind(currentValue);
    setTrusteeKindModal({
      open: true,
      arrIdx,
      partyId,
      value: normalizedCurrentValue || "individual",
    });
  }

  function closeTrusteeKindModal() {
    setTrusteeKindModal({ open: false, arrIdx: -1, partyId: "", value: "" });
  }

  function applyTrusteeKindSelection(value) {
    const arrIdx = Number(trusteeKindModal.arrIdx);
    const partyId = String(trusteeKindModal.partyId || "");
    const normalizedValue = normalizeTrusteeKind(value) || "individual";
    if (arrIdx < 0 || !partyId) {
      closeTrusteeKindModal();
      return;
    }

    patchParty(arrIdx, partyId, {
      trusteeKind: normalizedValue,
      ...(normalizedValue === "company" ? { nationality: "" } : {}),
    });
    closeTrusteeKindModal();

    requestAnimationFrame(() => {
      const el = trusteeInputRefs.current[partyId];
      if (el && typeof el.focus === "function") {
        el.focus();
      }
    });
  }

  function deleteManualArrangement(arrRowId) {
    if (readOnly) return;
    const la = ensureLegalArrangementsShape(ownership);
    const list = Array.isArray(la.arrangements) ? la.arrangements : [];

    const target = list.find(
      (a) => String(a?._rowId || "") === String(arrRowId || "")
    );
    if (!target) return;

    // block deletion of auto-derived rows
    if (
      target?.autoCreated &&
      (target?.source === "shareholder" || target?.source === "account_holder")
    )
      return;

    const next = list.filter(
      (a) => String(a?._rowId || "") !== String(arrRowId || "")
    );
    setLegalArrangements(next);
  }

  function toggleBasisDropdown(rowId) {
    if (openBasisRowId === rowId) { setOpenBasisRowId(null); return; }
    const btn = basisBtnRefs.current[rowId];
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setBasisDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpenBasisRowId(rowId);
  }

  function addParty(arrIdx, role) {
    if (readOnly) return;

    const la = ensureLegalArrangementsShape(ownership);
    const list = Array.isArray(la.arrangements) ? la.arrangements : [];
    if (!list[arrIdx]) return;

    const next = list.slice();
    const arr = { ...(next[arrIdx] || {}) };
    const parties = Array.isArray(arr.parties) ? arr.parties.slice() : [];

    if (role === "Protector" && hasOptionalProtector(parties)) return;

    parties.push({
      _rowId: makeRowId("laP"),
      role,
      name: "",
      trusteeKind: "",
      nationality: "",
      domicile: "",
      autoCreated: !!arr.autoCreated,
    });

    arr.parties = parties;
    next[arrIdx] = arr;
    setLegalArrangements(next);
  }

  function updateParty(arrIdx, partyRowId, key, value) {
    if (readOnly) return;

    const la = ensureLegalArrangementsShape(ownership);
    const list = Array.isArray(la.arrangements) ? la.arrangements : [];
    if (!list[arrIdx]) return;

    const next = list.slice();
    const arr = { ...(next[arrIdx] || {}) };
    const parties = Array.isArray(arr.parties) ? arr.parties.slice() : [];

    const pIdx = parties.findIndex(
      (p) => String(p?._rowId || "") === String(partyRowId || "")
    );
    if (pIdx === -1) return;

    parties[pIdx] = { ...(parties[pIdx] || {}), ...(typeof key === "object" ? key : { [key]: value }) };
    arr.parties = parties;
    next[arrIdx] = arr;
    setLegalArrangements(next);
  }

  function patchParty(arrIdx, partyRowId, patch) {
    if (readOnly) return;

    const la = ensureLegalArrangementsShape(ownership);
    const list = Array.isArray(la.arrangements) ? la.arrangements : [];
    if (!list[arrIdx]) return;

    const next = list.slice();
    const arr = { ...(next[arrIdx] || {}) };
    const parties = Array.isArray(arr.parties) ? arr.parties.slice() : [];

    const pIdx = parties.findIndex(
      (p) => String(p?._rowId || "") === String(partyRowId || "")
    );
    if (pIdx === -1) return;

    parties[pIdx] = { ...(parties[pIdx] || {}), ...(patch || {}) };
    arr.parties = parties;
    next[arrIdx] = arr;
    setLegalArrangements(next);
  }

  function deleteParty(arrIdx, partyRowId) {
    if (readOnly) return;

    const la = ensureLegalArrangementsShape(ownership);
    const list = Array.isArray(la.arrangements) ? la.arrangements : [];
    if (!list[arrIdx]) return;

    const next = list.slice();
    const arr = { ...(next[arrIdx] || {}) };
    const parties = Array.isArray(arr.parties) ? arr.parties.slice() : [];

    const target = parties.find(
      (p) => String(p?._rowId || "") === String(partyRowId || "")
    );
    if (!target) return;

    const role = String(target?.role || "");
    const base = baseRolesForType(arr.arrangementType);

    if (base.includes(role) && role !== "Beneficiary") return;

    if (role === "Beneficiary") {
      const benCount = parties.filter(
        (p) => String(p?.role || "") === "Beneficiary"
      ).length;
      if (benCount <= 1) return;
    }

    arr.parties = parties.filter(
      (p) => String(p?._rowId || "") !== String(partyRowId || "")
    );
    next[arrIdx] = arr;
    setLegalArrangements(next);
  }

  const TrashBtn = ({ onClick, disabled }) => (
    <button
      type="button"
      className="btn btn-sm btn-delete-trash p-0 d-inline-flex align-items-center justify-content-center"
      aria-label="Remove row"
      title="Remove row"
      onClick={onClick}
      disabled={disabled}
    >
      <i className="bi bi-trash" />
    </button>
  );

  const actionTh = !readOnly ? <th className="kycTblActionsTh" /> : null;

  return (
    <div>
      {!hideSectionHeadings ? (
        <div className="OwnershipMainSectionTitle" style={{ marginBottom: 8 }}>
          Controlling Persons
        </div>
      ) : null}

      {/* ✅ Toolbar: ONE dropdown only (hidden in clientMode — buttons appear inline per section) */}
      {!readOnly && allowAddButtons && !clientMode ? (
        <div className="d-flex gap-2 flex-wrap mb-2 align-items-center">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Related shareholder:</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 280 }}
              value={selectedRelated}
              onChange={(e) => setSelectedRelated(e.target.value)}
            >
              {relatedEntityOptions.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={addNaturalPersonRow}
            disabled={!hasSelectedRelated}
          >
            <i className="bi bi-plus-lg me-2" />
            Add Natural Person
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => addManualArrangement("Trust")}
            disabled={!selectedRelatedIsEntity}
          >
            <i className="bi bi-plus-lg me-2" />
            Add Trust
          </button>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => addManualArrangement("Foundation")}
            disabled={!selectedRelatedIsEntity}
          >
            <i className="bi bi-plus-lg me-2" />
            Add Foundation
          </button>
        </div>
      ) : null}

      {/* Natural Persons */}
      {effectiveShowNaturalPersons && !accountHolderIsTF ? (
        <>
          <div className={clientMode ? "kycInfoViewLabel" : "text-muted kycOwnSmallNote mb-2"} style={clientMode ? { marginBottom: 8 } : undefined}>
            {clientMode ? "UBOs" : "Natural Persons"}
          </div>

          {clientMode ? (
            /* ---- CLIENT MODE: grouped card layout — flat 4-col grid matching direct shareholders ---- */
            <>
              {(() => {
                // Group UBOs by relatedShareholderRowId (preserving original index for updates)
                const groups = [];
                ubos.forEach((r, idx) => {
                  const key = String(r?.relatedShareholderRowId || "") || "__none__";
                  const existing = groups.find(g => g.key === key);
                  if (existing) {
                    existing.items.push({ r, idx });
                  } else {
                    const isAccountHolder = key === "__account_holder__";
                    const relLabel = optionLabelById(relatedEntityOptions, key) || (isAccountHolder ? "Account holder" : key === "__none__" ? "—" : key);
                    groups.push({ key, relLabel, isAccountHolder, items: [{ r, idx }] });
                  }
                });
                return (
                  <div className="d-grid mb-2" style={{ gap: 12 }}>
                    {groups.map(group => (
                      <div key={group.key} className={`kycOwnershipPartyCard kycLegalArrangementCard kycOwnership4ColCard kycUboCard kycCpUboGroupEditCard${hideDateOfBirth ? " kycCpNoDob" : ""}`}>
                        {/* Col 1: Related shareholder (identity, blue bg) — spans all UBO rows */}
                        <div
                          className="kycOwnershipCellIdentity_blue"
                          style={{ gridRow: `1 / span ${group.items.length}` }}
                        >
                          <div className="kycInfoItem kycInfoItemAlignTop">
                            <div className="kycInfoIconDarkBlue">
                              <img src={iconEntity} alt="" className="kycInfoIconImg" />
                            </div>
                            <div className="kycInfoContent">
                              <div className="kycInfoViewLabel">{group.isAccountHolder ? "Related account holder" : "Related shareholder"}</div>
                              <div className="kycInfoViewText kycCpGroupRelLabel" style={{ fontSize: 14 }}>{group.relLabel}</div>
                            </div>
                          </div>
                        </div>

                        {/* Cols 2–4: one row-wrapper per UBO spanning all three columns */}
                        {group.items.map(({ r, idx }, rowIdx) => {
                          const rowId = r?._rowId || `ubo_idx_${idx}`;
                          const isQualified = isQualifiedUboRow(r, qualifiedNodeIds);
                          const dobLabel = formatCompactDob(r?.dobOrIncorporationDate);
                          const percentage = controllingPersonPercentageContext(ownership, ownershipSnapshot, idx);
                          return (
                            <div
                              key={rowId}
                              className={
                                "kycCpUboRowWrap" +
                                (changedUboRowIdSet.has(String(rowId))
                                  ? " kycChangedWrap"
                                  : "")
                              }
                              style={{ gridColumn: hideDateOfBirth ? "2 / 5" : "2 / 6", gridRow: rowIdx + 1 }}
                            >
                              {changedUboRowIdSet.has(String(rowId)) ? (
                                <img
                                  src={iconRightArrow}
                                  className="kycChangedRowArrow kycChangedRowArrow--left"
                                  alt=""
                                  aria-hidden="true"
                                />
                              ) : null}
                              {!readOnly ? (
                                <button type="button" className="kycCpUboRowDeleteBtn" onClick={() => deleteUbo(rowId)} aria-label="Remove">
                                  <i className="bi bi-trash3" aria-hidden="true" />
                                </button>
                              ) : null}

                              {/* Sub-col 1: Basis + Name */}
                              <div style={{ minWidth: 0 }}>
                                <div className="kycInfoItem kycInfoItemAlignTop">
                                  <div
                                    className={`kycInfoIconGrey${
                                      isQualified ? " kycQualifiedUboAvatarIconBox" : ""
                                    }`}
                                    style={!readOnly ? { cursor: "pointer" } : undefined}
                                    onClick={!readOnly ? () => toggleBasisDropdown(rowId) : undefined}
                                  >
                                    <img src={iconIndividual} alt="" className="kycInfoIconImg" />
                                    {isQualified ? <QualifiedUboBadge /> : null}
                                  </div>
                                  <div className="kycInfoContent">
                                    <div className="kycCpBasisSelectWrap">
                                      {readOnly ? (
                                        <div className="kycInfoViewLabel kycQualifiedUboLabel">
                                          <span>{r?.basisOfControl || "—"}</span>
                                        </div>
                                      ) : (
                                        <button
                                          ref={(el) => { basisBtnRefs.current[rowId] = el; }}
                                          type="button"
                                          className="kycCpBasisBtn"
                                          onClick={() => toggleBasisDropdown(rowId)}
                                        >
                                          {r?.basisOfControl || "Basis of control"}
                                          <span className="kycCpBasisArrow" aria-hidden="true">▾</span>
                                        </button>
                                      )}
                                      {percentage && (readOnly || onOwnershipPercentageChange) && (
                                        <OwnershipPercentageField
                                          {...percentage}
                                          personName={r.name}
                                          readOnly={readOnly}
                                          onCommit={(value) => onOwnershipPercentageChange(percentage.edge, value)}
                                        />
                                      )}
                                      {openBasisRowId === rowId && createPortal(
                                        <>
                                          <div className="kycShTypeDismiss" onClick={() => setOpenBasisRowId(null)} />
                                          <div className="kycShTypeDropdown" style={{ position: "fixed", top: basisDropdownPos.top, left: basisDropdownPos.left }}>
                                            {BASIS_OPTIONS.filter(o => o.v).map((o) => (
                                              <button key={o.v} type="button" className={"kycShTypeOption" + (r?.basisOfControl === o.v ? " is-active" : "")} onClick={() => { updateUbo(idx, "basisOfControl", o.v); setOpenBasisRowId(null); }}>
                                                {o.label}
                                              </button>
                                            ))}
                                          </div>
                                        </>,
                                        document.body
                                      )}
                                    </div>
                                    <div className="kycQualifiedUboInputWrap">
                                      {separateNames && !readOnly ? (
                                        <IndividualNamePreview
                                          inputClass="form-control kycCustomerDetailsHybridInput--line"
                                          displayName={r?.name || ""}
                                          value={r} onChange={(patch) => updateUbo(idx, patch)}
                                        />
                                      ) : (
                                        <input
                                          className="form-control kycCustomerDetailsHybridInput--line"
                                          value={r?.name ?? ""}
                                          disabled={readOnly}
                                          onChange={(e) => updateUbo(idx, "name", toTitleCase(e.target.value))}
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {hideDateOfBirth ? null : (
                              <div className="kycCpDobCell">
                                <div className="kycInfoItem kycInfoItemAlignTop">
                                  <div className="kycInfoIconGrey">
                                    <img src={iconDateOfBirth} alt="" className="kycInfoIconImg" />
                                  </div>
                                  <div className="kycInfoContent">
                                    <label className="kycInfoViewLabel" htmlFor={`cp-dob-${rowId}`}>Date of birth</label>
                                    {readOnly ? <span className="kycInfoViewText">{dobLabel || '—'}</span> : (
                                      <DatePickerField
                                        inputId={`cp-dob-${rowId}`}
                                        value={toDobDate(r?.dobOrIncorporationDate)}
                                        formatDisplayValue={formatCompactDob}
                                        onChange={(value) => updateUbo(idx, 'dobOrIncorporationDate', value)}
                                        placeholder="Select date"
                                        maxDate={new Date()}
                                        hideTriggerButton
                                        stagedView
                                        className="kycShDobPicker"
                                        inputClassName="kycCustomerDetailsHybridInput--line"
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                              )}

                              {/* Sub-col 2: Nationality — 150px */}
                              <div style={{ minWidth: 0 }}>
                                <div className="kycInfoItem kycInfoItemAlignTop">
                                  <div className="kycInfoIconGrey">
                                    <img src={iconPassport} alt="" className="kycInfoIconImg" />
                                  </div>
                                  <div className="kycInfoContent">
                                    <div className="kycInfoViewLabel">Nationality</div>
                                    <div data-tooltip-id={`cp-nat-${rowId}`} data-tooltip-content={countryLabel(r?.nationality)}>
                                      <CountrySelect value={r?.nationality ?? ""} onChange={(val) => updateUbo(idx, "nationality", val || "")} singleValueMode="iso3" className="kycCustomerDetailsHybridCountrySelect" disabled={readOnly} menuMinWidth={150} optionFontSize={12} />
                                    </div>
                                  </div>
                                </div>
                                {r?.nationality ? <Tooltip id={`cp-nat-${rowId}`} place="bottom" style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} /> : null}
                              </div>

                              {/* Sub-col 3: Domicile — 150px */}
                              <div style={{ minWidth: 0 }}>
                                <div className="kycInfoItem kycInfoItemAlignTop">
                                  <div className="kycInfoIconGrey">
                                    <img src={iconAddress} alt="" className="kycInfoIconImg" />
                                  </div>
                                  <div className="kycInfoContent">
                                    <div className="kycInfoViewLabel">Domicile</div>
                                    <div data-tooltip-id={`cp-dom-${rowId}`} data-tooltip-content={countryLabel(r?.countryOfResidence)}>
                                      <CountrySelect value={r?.countryOfResidence ?? ""} onChange={(val) => updateUbo(idx, "countryOfResidence", val || "")} singleValueMode="iso3" className="kycCustomerDetailsHybridCountrySelect" disabled={readOnly} menuMinWidth={150} optionFontSize={12} />
                                    </div>
                                  </div>
                                </div>
                                {r?.countryOfResidence ? <Tooltip id={`cp-dom-${rowId}`} place="bottom" style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} /> : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                    {ubos.length === 0 ? <div style={{ color: "#9aa5b0", fontSize: 14 }}>—</div> : null}
                  </div>
                );
              })()}

            </>
          ) : (
            /* ---- TABLE LAYOUT (staff KYC page — unchanged) ---- */
            <div className={`table-responsive kycTableWrap mb-3 ${naturalPersonsWrapClassName || ""}`}>
              <table className="table table-sm align-middle kycTbl kycTblUbos">
                <colgroup>
                  <col className="c-related" />
                  <col className="c-name" />
                  <col className="c-nat" />
                  <col className="c-res" />
                  <col className="c-basis" />
                  {!readOnly ? <col className="c-actions" /> : null}
                </colgroup>

                <thead>
                  <tr>
                    <th>Related shareholder</th>
                    <th>Name</th>
                    <th>Nationality</th>
                    <th>Country of residence</th>
                    <th>Basis of control</th>
                    {actionTh}
                  </tr>
                </thead>

                <tbody>
                  {ubos.length === 0 ? (
                    <tr>
                      <td colSpan={readOnly ? 5 : 6} className="text-muted">
                        No natural persons.
                      </td>
                    </tr>
                  ) : (
                    ubos.map((r, idx) => {
                      const rowId = r?._rowId || `ubo_idx_${idx}`;
                      const relId = String(r?.relatedShareholderRowId || "");
                      const relLabel = optionLabelById(relatedEntityOptions, relId) || "—";
                      const isQualified = isQualifiedUboRow(r, qualifiedNodeIds);

                      return (
                        <tr key={rowId}>
                          <td className="align-middle">
                            <span className="small">{relLabel}</span>
                          </td>

                          <td className="align-middle">
                            <div className="kycQualifiedUboInputWrap">
                              {separateNames && !readOnly ? (
                                <IndividualNamePreview
                                  inputClass="form-control form-control-sm"
                                  displayName={r?.name || ""}
                                          value={r} onChange={(patch) => updateUbo(idx, patch)}
                                />
                              ) : (
                                <input className="form-control form-control-sm" value={r?.name ?? ""} disabled={readOnly} onChange={(e) => updateUbo(idx, "name", toTitleCase(e.target.value))} />
                              )}
                            </div>
                          </td>

                          <td className="align-middle">
                            <CountrySelect value={r?.nationality ?? ""} onChange={(val) => updateUbo(idx, "nationality", val || "")} className={countrySelectLikeInputClass} optionTextClassName="countryTextOptionsOwnership" disabled={readOnly} />
                          </td>

                          <td className="align-middle">
                            <CountrySelect value={r?.countryOfResidence ?? ""} onChange={(val) => updateUbo(idx, "countryOfResidence", val || "")} className={countrySelectLikeInputClass} optionTextClassName="countryTextOptionsOwnership" disabled={readOnly} />
                          </td>

                          <td className="align-middle">
                            <select className="form-select form-select-sm" value={r?.basisOfControl ?? ""} disabled={readOnly} onChange={(e) => updateUbo(idx, "basisOfControl", e.target.value)}>
                              {BASIS_OPTIONS.map((o) => (
                                <option key={o.v} value={o.v}>{o.label}</option>
                              ))}
                            </select>
                          </td>

                          {!readOnly ? (
                            <td className="kycTblActionsTd">
                              <TrashBtn onClick={() => deleteUbo(rowId)} />
                            </td>
                          ) : null}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      {/* Legal Arrangements */}
      {effectiveShowLegalArrangements ? (
        <>
          {/* {accountHolderIsTF ? (
            <div className="alert alert-secondary py-2 mb-3">
              Since the account holder is a <b>{accountHolderTFType}</b>, only
              the legal arrangement details are required below.
            </div>
          ) : null} */}

          {!hideSectionHeadings ? (
            <div className="text-muted kycOwnSmallNote mb-2">
              Legal Arrangements (Trusts & Foundations)
            </div>
          ) : null}

          {clientMode ? (
            /* ---- CLIENT MODE: card layout ---- */
            <>
              <div className="d-grid mb-2" style={{ gap: 12 }}>
                {legalArrangements.map((arr, arrIdx) => {
                  const arrId = arr?._rowId || `la_${arrIdx}`;
                  const isAuto = !!arr?.autoCreated && (arr?.source === "shareholder" || arr?.source === "account_holder");
                  const parties = Array.isArray(arr?.parties) ? arr.parties : [];
                  const protectorExists = hasOptionalProtector(parties);
                  const badgeText = arr?.arrangementType || "Trust";
                  const canDeleteArrangement = !readOnly && !isAuto;
                  const isShareholderDerived = String(arr?.source || "") === "shareholder" && !!arr?.autoCreated;
                  const relLabel = String(arr?.relatedShareholderName || "").trim() || "—";
                  const arrangementName = toTitleCase(String(arr?.name || "").trim());
                  const arrangementTrustResolutionKind = String(arr?._accountHolderTrustResolutionKind || "").trim();
                  const effectiveAccountHolderTrustResolutionKind = String(arrangementTrustResolutionKind || accountHolderTrustResolutionKind || "").trim();
                  const isDerivedAccountHolderTrustName = accountHolderIsTF && String(arr?.source || "") === "account_holder" && effectiveAccountHolderTrustResolutionKind === "trust";
                  const isDerivedAccountHolderFoundationName = accountHolderIsTF && String(arr?.source || "") === "account_holder" && badgeText === "Foundation" && !!String(arr?._autoAccountHolderFoundationName || "");
                  const showDerivedArrangementName = isShareholderDerived || isDerivedAccountHolderTrustName || isDerivedAccountHolderFoundationName;
                  const arrangementPercentage = arrangementPercentageContext(ownership, ownershipSnapshot, arrIdx);

                  return (
                    <div key={arrId} className="kycOwnershipPartyCard kycLegalArrangementViewCard kycCpLaEditCard">
                      {canDeleteArrangement ? (
                        <button type="button" className="kycCpEditDeleteBtn" onClick={() => deleteManualArrangement(arrId)} aria-label="Remove arrangement">
                          <i className="bi bi-trash3" aria-hidden="true" />
                        </button>
                      ) : null}

                      {/* Col 1: Arrangement identity */}
                      <div className="kycOwnershipCellIdentity_blue">
                        <div className="kycCpLaIdentityStack">
                          <div className="kycInfoItem kycInfoItemAlignTop">
                            <div className="kycInfoIconDarkBlue">
                              <img src={iconTrust} alt="" className="kycInfoIconImg" />
                            </div>
                            <div className="kycInfoContent">
                              <div className="kycCpLaArrangementBlock">
                                <div className="kycInfoViewLabel">{badgeText}</div>
                                {showDerivedArrangementName ? (
                                  <div className="kycInfoViewText" style={{ fontSize: 14 }}>{arrangementName || "—"}</div>
                                ) : (
                                  <input
                                    className="form-control kycCustomerDetailsHybridInput--line"
                                    value={arr?.name ?? ""}
                                    disabled={readOnly}
                                    onChange={(e) => updateArrangement(arrIdx, "name", toTitleCase(e.target.value))}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                          {!accountHolderIsTF ? (
                            <div className="kycInfoItem kycInfoItemAlignTop">
                              <div className="kycInfoIconDarkBlue kycCpRelatedIcon">
                                <img src={iconEntity} alt="" className="kycInfoIconImg" />
                              </div>
                              <div className="kycInfoContent">
                                <div className="kycCpLaRelatedHead">
                                  <div className="kycInfoViewLabel">Related shareholder</div>
                                  {arrangementPercentage && (readOnly || onOwnershipPercentageChange) && (
                                    <OwnershipPercentageField
                                      {...arrangementPercentage}
                                      personName={arr?.name}
                                      readOnly={readOnly}
                                      onCommit={(value) => onOwnershipPercentageChange(arrangementPercentage.edge, value)}
                                    />
                                  )}
                                </div>
                                <div className="kycCpRelatedLabel">{relLabel}</div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {/* Col 2: Parties */}
                      <div className="kycCpLaPartiesCol">
                        {parties.map((p) => {
                          const pid = p?._rowId || makeRowId("laP");
                          const role = String(p?.role || "");
                          const base = baseRolesForType(arr?.arrangementType);
                          const isMandatory = base.includes(role) && role !== "Beneficiary";
                          const isDerivedAccountHolderTrustee = accountHolderIsTF && String(arr?.source || "") === "account_holder" && effectiveAccountHolderTrustResolutionKind === "trustee" && role === "Trustee";
                          const isTrusteeRole = role === "Trustee";
                          const isManualTrustee = isTrusteeRole && !isDerivedAccountHolderTrustee;
                          const trusteeKind = normalizeTrusteeKind(p?.trusteeKind);
                          const isCorporateTrustee = isTrusteeRole && trusteeKind === "company";
                          const dobLabel = !isCorporateTrustee
                            ? formatTinyDob(p?.dobOrIncorporationDate)
                            : "";
                          const roleLabel =
                            isTrusteeRole && trusteeKind
                              ? `${role} (${trusteeKindLabel(trusteeKind)})`
                              : role;
                          const trusteeFieldDisabled = readOnly || isDerivedAccountHolderTrustee;
                          const canDelete = !readOnly && !isMandatory && (role === "Beneficiary" || role === "Protector") &&
                            !(role === "Beneficiary" && parties.filter(x => String(x?.role || "") === "Beneficiary").length <= 1);

                          return (
                            <div key={pid} className="kycCpPartyRow">
                              {dobLabel ? (
                                <div className="kycPartyTinyDob">DOB: {dobLabel}</div>
                              ) : null}
                              {/* Cell 1: Role + name */}
                              <div className="kycInfoItem kycInfoItemAlignTop">
                                <div className="kycInfoIconGrey">
                                  <img src={cpRoleIcon(role)} alt="" className="kycInfoIconImg" />
                                </div>
                                <div className="kycInfoContent">
                                  <div className="kycInfoViewLabel">
                                    {roleLabel}
                                  </div>
                                  <div style={{ position: "relative" }}>
                                    {separateNames && !trusteeFieldDisabled && trusteeKind !== "company" && (!isTrusteeRole || trusteeKind === "individual") ? (
                                      <IndividualNamePreview
                                        inputClass="form-control kycCustomerDetailsHybridInput--line"
                                        displayName={p?.name || ""}
                                        value={p} onChange={(patch) => updateParty(arrIdx, pid, patch)}
                                      />
                                    ) : (
                                    <input
                                      ref={(el) => { if (el) trusteeInputRefs.current[pid] = el; else delete trusteeInputRefs.current[pid]; }}
                                      className="form-control kycCustomerDetailsHybridInput--line"
                                      value={toTitleCase(p?.name ?? "")}
                                      disabled={trusteeFieldDisabled}
                                      onMouseDown={(e) => {
                                        if (!readOnly && isManualTrustee && !trusteeKind) { e.preventDefault(); openTrusteeKindModal(arrIdx, pid, trusteeKind); }
                                      }}
                                      onFocus={(e) => {
                                        if (!readOnly && isManualTrustee && !trusteeKind) { e.target.blur(); openTrusteeKindModal(arrIdx, pid, trusteeKind); }
                                      }}
                                      onChange={(e) => updateParty(arrIdx, pid, "name", toTitleCase(e.target.value))}
                                    />
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Cell 2: Nationality */}
                              <div>
                                <div className="kycInfoItem kycInfoItemAlignTop">
                                  <div className="kycInfoIconGrey">
                                    <img src={iconPassport} alt="" className="kycInfoIconImg" />
                                  </div>
                                  <div className="kycInfoContent">
                                    <div className="kycInfoViewLabel">Nationality</div>
                                    {isCorporateTrustee ? (
                                      <span className="kycInfoViewText text-muted" style={{ fontSize: 14 }}>N.A.</span>
                                    ) : (
                                      <div data-tooltip-id={`la-nat-${pid}`} data-tooltip-content={countryLabel(p?.nationality)}>
                                        <CountrySelect value={p?.nationality ?? ""} onChange={(val) => updateParty(arrIdx, pid, "nationality", val || "")} singleValueMode="iso3" className="kycCustomerDetailsHybridCountrySelect" disabled={readOnly} menuMinWidth={150} optionFontSize={12} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {p?.nationality && !isCorporateTrustee ? <Tooltip id={`la-nat-${pid}`} place="bottom" style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} /> : null}
                              </div>

                              {/* Cell 3: Domicile */}
                              <div>
                                <div className="kycInfoItem kycInfoItemAlignTop">
                                  <div className="kycInfoIconGrey">
                                    <img src={iconAddress} alt="" className="kycInfoIconImg" />
                                  </div>
                                  <div className="kycInfoContent">
                                    <div className="kycInfoViewLabel">Domicile</div>
                                    <div data-tooltip-id={`la-dom-${pid}`} data-tooltip-content={countryLabel(p?.domicile)}>
                                      <CountrySelect value={p?.domicile ?? ""} onChange={(val) => updateParty(arrIdx, pid, "domicile", val || "")} singleValueMode="iso3" className="kycCustomerDetailsHybridCountrySelect" disabled={readOnly} menuMinWidth={150} optionFontSize={12} />
                                    </div>
                                  </div>
                                </div>
                                {p?.domicile ? <Tooltip id={`la-dom-${pid}`} place="bottom" style={{ zIndex: 9999, backgroundColor: "#9E9692", borderRadius: 10, fontSize: 12 }} /> : null}
                              </div>

                              {/* Cell 4: Delete */}
                              <div className="kycCpPartyDeleteCell">
                                {canDelete ? (
                                  <button type="button" className="kycCpPartyDeleteBtn" onClick={() => deleteParty(arrIdx, pid)}>
                                    <i className="bi bi-trash3" />
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {!readOnly ? (
                        <div className="kycCpLaActions" style={{ gridColumn: "1 / -1", justifyContent: "flex-end" }}>
                          <button type="button" className="kycShEditAddBtn kycCpLaAddBtn" onClick={() => addParty(arrIdx, "Beneficiary")}>
                            <i className="bi bi-plus-lg me-1" /> Add beneficiary
                          </button>
                          <button type="button" className="kycShEditAddBtn kycCpLaAddBtn" disabled={protectorExists} onClick={() => addParty(arrIdx, "Protector")}>
                            <i className="bi bi-plus-lg me-1" /> Add protector
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
                {legalArrangements.length === 0 ? <div style={{ color: "#9aa5b0", fontSize: 14 }}>—</div> : null}
              </div>

            </>
          ) : (
            /* ---- TABLE LAYOUT (staff KYC page — unchanged) ---- */
            <div className="table-responsive kycTableWrap mb-2">
              <table className="table table-sm align-middle kycTbl">
                <tbody>
                  {legalArrangements.length === 0 ? (
                    <tr>
                      <td className="text-muted">No legal arrangements.</td>
                    </tr>
                  ) : (
                    legalArrangements.map((arr, arrIdx) => {
                      const arrId = arr?._rowId || `la_${arrIdx}`;
                      const isAuto = !!arr?.autoCreated && (arr?.source === "shareholder" || arr?.source === "account_holder");
                      const parties = Array.isArray(arr?.parties) ? arr.parties : [];
                      const protectorExists = hasOptionalProtector(parties);
                      const badgeText = arr?.arrangementType || "Trust";
                      const canDeleteArrangement = !readOnly && !isAuto;
                      const isShareholderDerived = String(arr?.source || "") === "shareholder" && !!arr?.autoCreated;
                      const relLabel = String(arr?.relatedShareholderName || "").trim() || "—";
                      const placeholder = badgeText === "Foundation" ? "Enter foundation name" : "Enter trust name";
                      const arrangementName = toTitleCase(String(arr?.name || "").trim());
                      const arrangementTrustResolutionKind = String(arr?._accountHolderTrustResolutionKind || "").trim();
                      const effectiveAccountHolderTrustResolutionKind = String(arrangementTrustResolutionKind || accountHolderTrustResolutionKind || "").trim();
                      const isDerivedAccountHolderTrustName = accountHolderIsTF && String(arr?.source || "") === "account_holder" && effectiveAccountHolderTrustResolutionKind === "trust";
                      const isDerivedAccountHolderFoundationName = accountHolderIsTF && String(arr?.source || "") === "account_holder" && badgeText === "Foundation" && !!String(arr?._autoAccountHolderFoundationName || "");
                      const showDerivedArrangementName = isShareholderDerived || isDerivedAccountHolderTrustName || isDerivedAccountHolderFoundationName;

                      return (
                        <React.Fragment key={arrId}>
                          <tr className="table-light">
                            <td>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="badge bg-light text-dark border">{badgeText}</span>

                                {showDerivedArrangementName ? (
                                  <span className="small text-muted">
                                    {badgeText === "Foundation" ? "Foundation name: " : "Trust name: "}
                                    <b className="text-dark">{arrangementName || "—"}</b>
                                  </span>
                                ) : (
                                  <div style={{ minWidth: 240, flex: "1 1 280px" }}>
                                    <input className="form-control form-control-sm" value={arr?.name ?? ""} disabled={readOnly} placeholder={placeholder} onChange={(e) => updateArrangement(arrIdx, "name", toTitleCase(e.target.value))} />
                                  </div>
                                )}

                                {!accountHolderIsTF ? (
                                  <span className="small text-muted">Related shareholder: <b className="text-dark">{relLabel}</b></span>
                                ) : null}

                                {!readOnly ? (
                                  <>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addParty(arrIdx, "Beneficiary")}>
                                      <i className="bi bi-plus-lg me-2" />Add beneficiary
                                    </button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" disabled={protectorExists} onClick={() => addParty(arrIdx, "Protector")}>
                                      <i className="bi bi-plus-lg me-2" />Add protector
                                    </button>
                                    {isAuto ? <span className="text-muted small">Auto-derived</span> : null}
                                    <div className="ms-auto">
                                      {canDeleteArrangement ? <TrashBtn onClick={() => deleteManualArrangement(arrId)} /> : <span className="text-muted small">—</span>}
                                    </div>
                                  </>
                                ) : null}
                              </div>
                            </td>
                          </tr>

                          <tr>
                            <td className="py-0">
                              <div className="table-responsive" style={{ marginTop: 10 }}>
                                <table className="table table-sm mb-0">
                                  <colgroup>
                                    <col />
                                    <col style={{ width: "240px" }} />
                                    <col style={{ width: "260px" }} />
                                    <col style={{ width: "210px" }} />
                                    {!readOnly ? <col style={{ width: "60px" }} /> : null}
                                  </colgroup>
                                  <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Nationality</th>
                                      <th>Domicile</th>
                                      <th>Role</th>
                                      {!readOnly ? <th /> : null}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {parties.map((p) => {
                                      const pid = p?._rowId || makeRowId("laP");
                                      const role = String(p?.role || "");
                                      const base = baseRolesForType(arr?.arrangementType);
                                      const isMandatory = base.includes(role) && role !== "Beneficiary";
                                      const isDerivedAccountHolderTrustee = accountHolderIsTF && String(arr?.source || "") === "account_holder" && effectiveAccountHolderTrustResolutionKind === "trustee" && role === "Trustee";
                                      const isTrusteeRole = role === "Trustee";
                                      const isManualTrustee = isTrusteeRole && !isDerivedAccountHolderTrustee;
                                      const trusteeKind = normalizeTrusteeKind(p?.trusteeKind);
                                      const isCorporateTrustee = isTrusteeRole && trusteeKind === "company";
                                      const showTrusteeKindBadge = isTrusteeRole && !!trusteeKind;
                                      const trusteeFieldDisabled = readOnly || isDerivedAccountHolderTrustee;
                                      const canDelete = !readOnly && !isMandatory && (role === "Beneficiary" || role === "Protector") &&
                                        !(role === "Beneficiary" && parties.filter(x => String(x?.role || "") === "Beneficiary").length <= 1);

                                      return (
                                        <tr key={pid}>
                                          <td>
                                            <div style={{ position: "relative" }}>
                                              <input
                                                ref={(el) => { if (el) trusteeInputRefs.current[pid] = el; else delete trusteeInputRefs.current[pid]; }}
                                                className="form-control form-control-sm"
                                                value={toTitleCase(p?.name ?? "")}
                                                disabled={trusteeFieldDisabled}
                                                style={showTrusteeKindBadge ? { paddingRight: 96 } : undefined}
                                                onMouseDown={(e) => { if (!readOnly && isManualTrustee && !trusteeKind) { e.preventDefault(); openTrusteeKindModal(arrIdx, pid, trusteeKind); } }}
                                                onFocus={(e) => { if (!readOnly && isManualTrustee && !trusteeKind) { e.target.blur(); openTrusteeKindModal(arrIdx, pid, trusteeKind); } }}
                                                onChange={(e) => updateParty(arrIdx, pid, "name", toTitleCase(e.target.value))}
                                              />
                                              {showTrusteeKindBadge ? (
                                                <button type="button" className="badge bg-light text-dark border" disabled={trusteeFieldDisabled}
                                                  onClick={() => { if (!trusteeFieldDisabled) openTrusteeKindModal(arrIdx, pid, trusteeKind); }}
                                                  style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", borderRadius: 999, padding: "4px 10px", fontWeight: 500, cursor: trusteeFieldDisabled ? "default" : "pointer" }}>
                                                  {trusteeKindLabel(trusteeKind)}
                                                </button>
                                              ) : null}
                                            </div>
                                          </td>
                                          <td>
                                            {isCorporateTrustee ? (
                                              <div className="form-control-plaintext text-muted" style={{ padding: "0.2rem 0.35rem", lineHeight: 1.2, textAlign: "center" }}>N.A.</div>
                                            ) : (
                                              <CountrySelect value={p?.nationality ?? ""} onChange={(val) => updateParty(arrIdx, pid, "nationality", val || "")} className={countrySelectLikeInputClass} optionTextClassName="countryTextOptionsOwnership" disabled={readOnly} />
                                            )}
                                          </td>
                                          <td>
                                            <CountrySelect value={p?.domicile ?? ""} onChange={(val) => updateParty(arrIdx, pid, "domicile", val || "")} className={countrySelectLikeInputClass} optionTextClassName="countryTextOptionsOwnership" disabled={readOnly} />
                                          </td>
                                          <td className="align-middle">
                                            <span className="badge bg-light text-dark border">{role}</span>
                                          </td>
                                          {!readOnly ? (
                                            <td className="text-end">
                                              {canDelete ? <TrashBtn onClick={() => deleteParty(arrIdx, pid)} /> : <span className="text-muted small">—</span>}
                                            </td>
                                          ) : null}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      {clientMode && !readOnly && allowAddButtons ? (
        <div className="kycSigFooterRow" style={{ marginTop: 16 }}>
          {selectableRelatedOptions.filter(o => o.v).length > 1 ? (
            <div className="d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
              <span className="kycCpRelatedFooterLabel">Related shareholder:</span>
              <div className="kycCpRelatedSelectWrap">
                <button
                  ref={relatedBtnRef}
                  type="button"
                  className="kycCpBasisBtn kycCpRelatedBtn"
                  onClick={toggleRelatedDropdown}
                >
                  <span className="kycCpRelatedBtnText">{selectedRelatedLabel}</span>
                  <span className="kycCpBasisArrow" aria-hidden="true">▾</span>
                </button>
                {relatedDropdownOpen && createPortal(
                  <>
                    <div className="kycShTypeDismiss" onClick={() => setRelatedDropdownOpen(false)} />
                    <div
                      className="kycShTypeDropdown kycCpRelatedDropdown"
                      style={{
                        position: "fixed",
                        top: relatedDropdownPos.top,
                        left: relatedDropdownPos.left,
                        minWidth: relatedDropdownPos.width || 200,
                      }}
                    >
                      {selectableRelatedOptions.map((o) => (
                        <button
                          key={o.v || "__select__"}
                          type="button"
                          className={"kycShTypeOption" + (String(selectedRelated) === String(o.v || "") ? " is-active" : "")}
                          onClick={() => {
                            setSelectedRelated(o.v || "");
                            setRelatedDropdownOpen(false);
                          }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </>,
                  document.body
                )}
              </div>
            </div>
          ) : null}
          <button
            type="button"
            className="kycSigAddBtn"
            onClick={addNaturalPersonRow}
            disabled={!hasSelectedRelated}
          >
            <i className="bi bi-plus-lg me-2" aria-hidden="true" />
            Add Natural Person
          </button>
          <button
            type="button"
            className="kycSigAddBtn"
            onClick={() => addManualArrangement("Trust")}
            disabled={!selectedRelatedIsEntity}
          >
            <i className="bi bi-plus-lg me-2" aria-hidden="true" />
            Add Trust
          </button>
          <button
            type="button"
            className="kycSigAddBtn"
            onClick={() => addManualArrangement("Foundation")}
            disabled={!selectedRelatedIsEntity}
          >
            <i className="bi bi-plus-lg me-2" aria-hidden="true" />
            Add Foundation
          </button>
        </div>
      ) : null}

      <AnimatePresence>
        {trusteeKindModal.open ? (
          <motion.div
            className="otModalOverlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onMouseDown={closeTrusteeKindModal}
          >
            <motion.div
              className="otModalCard"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.985 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              onMouseDown={(e) => e.stopPropagation()}
              style={{ maxWidth: 460 }}
            >
              <div className="otModalHeader otModalPreviewHeader">
                <div className="otModalTitle otModalPreviewCenter">
                  Trustee type
                </div>
              </div>

              <div
                className="otModalSubtitle otModalPreviewCenter otModalPreviewSubtitle"
                style={{ fontSize: 16, marginBottom: 18 }}
              >
                Select whether the trustee is an individual or a company.
              </div>

              <div className="d-grid" style={{ gap: 12, marginBottom: 12 }}>
                {[
                  { value: "individual", label: "Individual" },
                  { value: "company", label: "Company" },
                ].map((opt) => {
                  const active = trusteeKindModal.value === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => applyTrusteeKindSelection(opt.value)}
                      style={{
                        textAlign: "left",
                        borderRadius: 14,
                        border: active
                          ? "1px solid #97A6B2"
                          : "1px solid rgba(0,0,0,0.08)",
                        background: active ? "#EEF3F6" : "#F6F7F9",
                        padding: "14px 16px",
                        fontFamily: "Roboto",
                        fontSize: 15,
                        fontWeight: active ? 600 : 500,
                        color: "#4A4F59",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
